import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { Subscription, SubscriptionPlan, SubscriptionCheckResult } from '../types';
import { API_BASE_URL } from '../types';

interface UseSubscriptionReturn {
  subscription: Subscription | null;
  hasActiveSubscription: boolean;
  requiresSubscription: boolean;
  isLoading: boolean;
  error: string | null;
  plans: SubscriptionPlan[];
  checkout: (planId: string) => Promise<string>;
  openCustomerPortal: () => Promise<string>;
  refetch: () => void;
}

export const useSubscription = (): UseSubscriptionReturn => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Check subscription status
  const {
    data: subscriptionData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['subscription', user?.uid],
    queryFn: async (): Promise<SubscriptionCheckResult> => {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await user.getIdToken();
      const response = await fetch(`${API_BASE_URL}/subscription/status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to check subscription status');
      }

      const data = await response.json();
      return data;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Get available plans
  const { data: plansData } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: async (): Promise<SubscriptionPlan[]> => {
      const response = await fetch(`${API_BASE_URL}/subscription/plans`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch subscription plans');
      }

      const data = await response.json();
      return data.plans;
    },
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
  });

  // Create checkout session mutation
  const checkoutMutation = useMutation({
    mutationFn: async (planId: string): Promise<string> => {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await user.getIdToken();
      const response = await fetch(`${API_BASE_URL}/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ planId }),
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      return data.url;
    },
    onSuccess: () => {
      // Refetch subscription status after successful checkout
      queryClient.invalidateQueries({ queryKey: ['subscription', user?.uid] });
    },
  });

  // Create customer portal session mutation
  const portalMutation = useMutation({
    mutationFn: async (): Promise<string> => {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await user.getIdToken();
      const response = await fetch(`${API_BASE_URL}/subscription/portal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to create customer portal session');
      }

      const data = await response.json();
      return data.url;
    },
  });

  return {
    subscription: subscriptionData?.subscription || null,
    hasActiveSubscription: subscriptionData?.hasActiveSubscription || false,
    requiresSubscription: subscriptionData?.requiresSubscription || true,
    isLoading,
    error: error instanceof Error ? error.message : null,
    plans: plansData || [],
    checkout: checkoutMutation.mutateAsync,
    openCustomerPortal: portalMutation.mutateAsync,
    refetch,
  };
}; 